import React from 'react';
import { X } from 'lucide-react';

const STORAGE_KEY = 'succeedo_profile_banner_dismissed';

/**
 * Determines which onboarding sections are incomplete based on currentUser fields.
 * Returns an array of human-readable section names.
 */
export function getIncompleteSections(currentUser) {
  const sections = [];
  if (!currentUser) return sections;

  // "About You" — needs company name or geography
  if (!currentUser.succeedo_company_name && !currentUser.province) {
    sections.push('About You');
  }

  // "Business Details" — needs industry_sector
  if (!currentUser.industry_sector) {
    sections.push('Business Details');
  }

  // "Buyer Criteria" — buyer/both persona with no buyer profile fields
  if (
    (currentUser.succeedo_persona === 'buyer' || currentUser.succeedo_persona === 'both') &&
    !currentUser.buyer_profile
  ) {
    sections.push('Buyer Criteria');
  }

  return sections;
}

/**
 * Returns true if the banner has been dismissed via localStorage.
 */
export function isBannerDismissed() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Persists the banner dismissal to localStorage.
 */
export function dismissBanner() {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // localStorage unavailable — banner will reappear next load
  }
}

/**
 * Dashboard banner nudging users to complete skipped onboarding steps.
 *
 * Props:
 *  - incompleteSections: string[] — e.g. ["About You", "Business Details"]
 *  - onDismiss: () => void
 */
export default function CompleteProfileBanner({ incompleteSections, onDismiss }) {
  if (!incompleteSections || incompleteSections.length === 0) return null;

  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-5 mb-8">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="text-[hsl(var(--foreground))] font-semibold text-base mb-1">
            Complete your profile
          </h3>
          <p className="text-muted-foreground text-sm mb-3">
            Finish setting up your profile to unlock better partner matching and personalized tool defaults.
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {incompleteSections.map((section) => (
              <span
                key={section}
                className="border border-[hsl(var(--succeedo-orange))]/20 text-[hsl(var(--succeedo-orange))] rounded-full px-3 py-1 text-[11px] font-mono uppercase"
              >
                {section}
              </span>
            ))}
          </div>
          <a
            href="/succeedo/profile"
            className="text-[hsl(var(--succeedo-orange))] text-sm font-medium hover:underline"
          >
            Complete Profile &rarr;
          </a>
        </div>
        <button
          onClick={onDismiss}
          className="text-muted-foreground hover:text-[hsl(var(--foreground))] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
